import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "use-intl";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ReviewLink } from "@/types/review";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";
import { RejectLinkDialog } from "./RejectLinkDialog";
import type { MatchWords } from "./useMatchLabels";

interface LinkedSuggestionPanelProps {
  suggestionId: string;
  link: ReviewLink;
  /** The matcher's own words, e.g. "tax account row" / "imported verification" / "tax account". */
  labels: MatchWords;
  formatCurrency: (amount: number, currency: string) => string;
  onRejected: () => void;
}

/**
 * FIN-1071 S2: an item a booking already in the books covers. It posts nothing; it points at that
 * booking (R8). See specs/generic-matching/screens.md.
 */
export function LinkedSuggestionPanel({ suggestionId, link, labels, formatCurrency, onRejected }: LinkedSuggestionPanelProps) {
  const t = useTranslations("consultantReview.match.linked");
  const formatOptionalDate = useFormatOptionalDate();
  const [showReject, setShowReject] = useState(false);
  const label = `${link.series}${link.number}`;
  const openVerification = (
    <Link
      to={`/bookkeeping/journal?entry=${encodeURIComponent(link.journalEntryId)}`}
      className="text-sm font-medium text-primary underline-offset-4 hover:underline"
    >
      {t("openVerification", { label })}
    </Link>
  );

  if (link.state === "rejected") {
    return (
      <Alert data-testid="linked-suggestion-rejected">
        <AlertTitle>{t("rejectedTitle")}</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>{t("rejectedBody", { label })}</p>
          {link.rejectionReason && (
            <p className="text-muted-foreground">{t("rejectedReason", { reason: link.rejectionReason })}</p>
          )}
          {openVerification}
        </AlertDescription>
      </Alert>
    );
  }

  if (link.state === "superseded") {
    return (
      <Alert data-testid="linked-suggestion-superseded">
        <AlertTitle>{t("endedTitle")}</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>{t("endedBody", { label })}</p>
          {openVerification}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4" data-testid="linked-suggestion">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("bookedTitle")}</h3>
        <p className="text-sm">{t("matchedTo", { target: labels.target, label, date: formatOptionalDate(link.date) })}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
        <div>
          <h4 className="text-sm font-semibold">{t("verification")}</h4>
          <p>{label}</p>
        </div>
        <div>
          <h4 className="text-sm font-semibold">{t("ledgerAmount", { ledger: labels.ledger })}</h4>
          <p>{formatCurrency(link.amountOre / 100, "SEK")}</p>
        </div>
        <div className="col-span-2">
          <Badge variant="outline">{t("postsNothing")}</Badge>
          <p className="text-sm text-muted-foreground mt-1">{t("targetIsBooking", { target: labels.target })}</p>
        </div>
        {link.explanation && (
          <div className="col-span-2">
            <h4 className="text-sm font-semibold">{t("why")}</h4>
            <p className="text-sm text-muted-foreground">{link.explanation}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 pt-4 border-t">
        {openVerification}
        <Button variant="outline" onClick={() => setShowReject(true)}>
          {t("rejectLink")}
        </Button>
      </div>

      <RejectLinkDialog
        open={showReject}
        onOpenChange={setShowReject}
        suggestionId={suggestionId}
        verificationLabel={label}
        itemLabel={labels.item}
        onRejected={() => {
          setShowReject(false);
          onRejected();
        }}
      />
    </div>
  );
}
